import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Use Cases
import {
  getVisibleMenuItems,
  getSearchableMenuItems,
  getFavoriteMenuLeaves,
  searchMenuLeaves,
  findMenuItemById,
  hasMenuChildren,
  countHiddenMenuLeaves,
  mapMenuLeavesToViews,
} from './menuNavigationUseCase';

// Utils
import { normalizeUrl } from '../../utils/url';

/**
 * @typedef {'menu' | 'favorites' | 'search'} MenuMode
 */

/**
 * Sidebar navigation state: modes (menu / favorites / search), L1 accordion, L2 popover.
 * @param {object} param0
 * @param {ApiManagerMenu | undefined} param0.menu
 * @param {boolean} param0.isMenuLoading
 * @param {boolean} [param0.keepOpen=false] - Prevent collapsing on mouse leave (e.g. modal open)
 * @returns {{
 *  mode: MenuMode,
 *  handleModeChange: (mode: MenuMode) => void,
 *  search: string,
 *  setSearch: (search: string) => void,
 *  navIsOpen: boolean,
 *  handleNavMouseEnter: () => void,
 *  handleNavMouseLeave: () => void,
 *  expandedItemId: string | null,
 *  handleRootItemClick: (item: ApiManagerMenuItem) => void,
 *  popoverItem: ApiManagerMenuItem | null,
 *  popoverHiddenCount: number,
 *  handleSubItemClick: (item: ApiManagerMenuItem) => void,
 *  closePopover: () => void,
 *  navigateToItemId: (itemId: string) => void,
 *  visibleItems: ApiManagerMenuItem[],
 *  bottomSectionItems: ApiManagerMenuItem[],
 *  favoriteLeaves: import('./menuNavigationUseCase').MenuLeafView[],
 *  searchLeaves: import('./menuNavigationUseCase').MenuLeafView[],
 * }}
 */
const useMenu = ({ menu, isMenuLoading, keepOpen = false }) => {
  const navigate = useNavigate();
  const [navIsOpen, setNavIsOpen] = useState(false);
  const [mode, setMode] = useState(/** @type {MenuMode} */ ('menu'));
  const [search, setSearch] = useState('');
  const [expandedItemId, setExpandedItemId] = useState(/** @type {string | null} */ (null));
  const [popoverItemId, setPopoverItemId] = useState(/** @type {string | null} */ (null));
  // Pointer over the sidebar or its (portaled) popover: no re-render needed
  const isPointerInsideRef = useRef(false);

  const visibleItems = useMemo(
    () => (isMenuLoading ? [] : getVisibleMenuItems(menu?.items)),
    [menu?.items, isMenuLoading],
  );

  const bottomSectionItems = useMemo(
    () => (isMenuLoading ? [] : getVisibleMenuItems(menu?.bottomSection?.items)),
    [menu?.bottomSection?.items, isMenuLoading],
  );

  const favoriteLeaves = useMemo(
    () => mapMenuLeavesToViews(getFavoriteMenuLeaves([...visibleItems, ...bottomSectionItems])),
    [visibleItems, bottomSectionItems],
  );
  // Search also finds the items hidden by the user (greyed, still navigable)
  const searchableItems = useMemo(
    () => (isMenuLoading ? [] : getSearchableMenuItems(menu?.items)),
    [menu?.items, isMenuLoading],
  );
  const searchLeaves = useMemo(
    () => mapMenuLeavesToViews(searchMenuLeaves(searchableItems, search)),
    [searchableItems, search],
  );
  const popoverItem = useMemo(
    () => findMenuItemById(visibleItems, popoverItemId)
      ?? findMenuItemById(bottomSectionItems, popoverItemId),
    [visibleItems, bottomSectionItems, popoverItemId],
  );
  // Counted on the raw menu: hidden children are stripped from `popoverItem`
  const popoverHiddenCount = useMemo(
    () => countHiddenMenuLeaves(
      findMenuItemById(menu?.items, popoverItemId) ?? findMenuItemById(menu?.bottomSection?.items, popoverItemId),
    ),
    [menu?.items, menu?.bottomSection?.items, popoverItemId],
  );

  const collapseNav = () => {
    setNavIsOpen(false);
    setExpandedItemId(null);
  };

  const handleNavMouseEnter = () => {
    isPointerInsideRef.current = true;
    setNavIsOpen(true);
  };

  const handleNavMouseLeave = () => {
    isPointerInsideRef.current = false;
    // The popover is portaled: leaving the sidebar towards it must not collapse the navigation
    if (keepOpen || popoverItemId) return;
    collapseNav();
  };

  const closePopover = () => {
    setPopoverItemId(null);
    // Dismissed from outside (click, Escape): no mouse leave will follow, collapse now
    if (!keepOpen && !isPointerInsideRef.current) collapseNav();
  };

  /**
   * @param {ApiManagerMenuItem} item
   */
  const navigateToItem = (item) => {
    if (!item.path) return;
    // Normalize path to ensure it starts with '/' for absolute navigation
    navigate(normalizeUrl(item.path));
    setPopoverItemId(null);
    setNavIsOpen(false);
    setExpandedItemId(null);
  };

  /**
   * Navigate to a visible item, a bottom section item or a hidden item (search result) by id.
   * @param {string} itemId
   */
  const navigateToItemId = (itemId) => {
    const item = findMenuItemById(visibleItems, itemId)
      ?? findMenuItemById(bottomSectionItems, itemId)
      ?? findMenuItemById(searchableItems, itemId);
    if (item) navigateToItem(item);
  };

  /**
   * @param {MenuMode} nextMode
   */
  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setNavIsOpen(true);
    setPopoverItemId(null);
  };

  /**
   * L1 item: toggle its accordion, or navigate when it has no children.
   * @param {ApiManagerMenuItem} item
   */
  const handleRootItemClick = (item) => {
    if (!hasMenuChildren(item)) {
      navigateToItem(item);
      return;
    }
    setNavIsOpen(true);
    setExpandedItemId((current) => (current === item.id ? null : item.id));
  };

  /**
   * L2 item: open its popover, or navigate when it has no children.
   * @param {ApiManagerMenuItem} item
   */
  const handleSubItemClick = (item) => {
    if (!hasMenuChildren(item)) {
      navigateToItem(item);
      return;
    }
    setPopoverItemId((current) => (current === item.id ? null : item.id));
  };

  return {
    mode,
    handleModeChange,
    search,
    setSearch,
    navIsOpen,
    handleNavMouseEnter,
    handleNavMouseLeave,
    expandedItemId,
    handleRootItemClick,
    popoverItem,
    popoverHiddenCount,
    handleSubItemClick,
    closePopover,
    navigateToItemId,
    visibleItems,
    bottomSectionItems,
    favoriteLeaves,
    searchLeaves,
  };
};

export default useMenu;
